import axios from 'axios';
import apiConfig from '../config/api.js';

// Create axios instance with default config
const api = axios.create({
  // Use relative paths to leverage Vercel rewrites
  baseURL: '/',
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    let sandboxOverride = null;
    if (typeof localStorage !== 'undefined') {
      const sandboxOverrideRaw = localStorage.getItem('__sandboxApiBaseOverride');
      if (sandboxOverrideRaw && sandboxOverrideRaw.trim()) {
        sandboxOverride = sandboxOverrideRaw.trim().replace(/\/+$/, '');
      }
    }

  const usingSandboxOverride = Boolean(sandboxOverride);

  if (usingSandboxOverride) {
    config.baseURL = sandboxOverride;
  }

    const token = localStorage.getItem('token');
    console.log('API Request:', config.method?.toUpperCase(), config.url, 'Token:', token ? 'exists' : 'missing');
    
    // Ensure all API requests get /api/ prefix to hit Vercel rewrites
  try {
    const isAbsoluteUrl = /^https?:\/\//i.test(config.url || '');
    if (!isAbsoluteUrl) {
      const originalUrl = config.url || '';
      // Only prefix if it's not already prefixed with /api/
      if (originalUrl && !originalUrl.startsWith('/api/')) {
        const needsSlash = originalUrl && !originalUrl.startsWith('/');
        config.url = `/api${needsSlash ? '/' : ''}${originalUrl}`;
      }
    }
  } catch (_) {}

  if (usingSandboxOverride && config.url && !config.url.startsWith('/')) {
    config.url = `/${config.url}`;
  }

    const skipAuthHeader = Boolean(config.__skipAuthHeader);
    if ('__skipAuthHeader' in config) {
      delete config.__skipAuthHeader;
    }

    if (skipAuthHeader) {
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      if (config.headers?.authorization) {
        delete config.headers.authorization;
      }
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    console.log('API Response Success:', response.config?.method?.toUpperCase(), response.config?.url, response.status);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status, error.response?.data);
    
    // Handle token expiration (401 = unauthorized, 403 = forbidden)
    if (error.response?.status === 401 || error.response?.status === 403) {
      const skipAuthTokenPurge = Boolean(error.config?.__skipAuthTokenPurge);
      // DON'T remove token for blob responses - let the caller handle it
      // Blob responses (like PDF downloads) need to read the error message first
      const isBlobResponse = error.config?.responseType === 'blob';
      const errorCode = error.response?.data?.code;
      const errorDetails = error.response?.data?.details || error.response?.data?.message || '';
      const errorMessage = String(errorDetails || '').toLowerCase();
      
      // Check if this is actually an authentication error or a request formatting error
      const isRequestFormatError = 
        errorMessage.includes('invalid key=value pair') ||
        errorMessage.includes('missing equal-sign') ||
        errorMessage.includes('malformed') ||
        errorMessage.includes('bad request');
      
      const isTransientInvalidToken =
        error.response?.status === 403 &&
        errorCode === 'INVALID_TOKEN' &&
        typeof errorDetails === 'string' &&
        errorDetails.toLowerCase().includes('connection terminated unexpectedly');
      
      // CRITICAL: Check for non-auth 403 errors (business logic errors, not authentication errors)
      // These should NOT trigger logout
      const isBusinessLogicError = 
        errorCode === 'DEFAULT_PAYMENT_METHOD_MISSING' ||
        errorCode === 'PAYMENT_METHOD_REQUIRED' ||
        errorCode === 'PAYMENT_METHOD_DELETED' ||
        errorCode === 'STRIPE_CONNECT_REQUIRED' ||
        errorCode === 'STRIPE_ACCOUNT_NOT_CONNECTED' ||
        errorCode === 'STRIPE_ACCOUNT_NOT_READY' ||
        errorCode === 'ALREADY_PAID' ||
        errorCode === 'INVOICE_UNAPPROVED' ||
        errorCode === 'AMOUNT_BELOW_MINIMUM' ||
        errorMessage.includes('payment method') ||
        errorMessage.includes('stripe connect') ||
        errorMessage.includes('already paid') ||
        errorMessage.includes('unapproved');
      
      // DON'T redirect for registration/login endpoints - let them handle errors themselves
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/register') || 
                            url.includes('/auth/login') || 
                            url.includes('/api/auth/register') || 
                            url.includes('/api/auth/login') ||
                            url.includes('/signup') ||
                            url.includes('/login');
      
      // Only treat as auth error if it's NOT a request formatting error AND NOT a business logic error
      const isRealAuthError = !isRequestFormatError && !isBusinessLogicError;
      
      if (!skipAuthTokenPurge && !isBlobResponse && !isAuthEndpoint && !isTransientInvalidToken && isRealAuthError) {
        console.error('API: Auth error (401/403) - removing token');
        console.error('API: Token before removal:', localStorage.getItem('token') ? 'exists' : 'missing');
        localStorage.removeItem('token');
        console.error('API: Token after removal:', localStorage.getItem('token') ? 'exists' : 'missing');
        
        // Only redirect if not already on login page or signup page
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/signup')) {
          const isStaffPortal = window.location.pathname.startsWith('/staff');
          window.location.href = isStaffPortal ? '/staff/login' : '/login';
        }
      } else if (isRequestFormatError) {
        // Request formatting error - don't log out, just log the error
        console.warn('API: Request formatting error (403) - keeping token, this is not an auth failure');
      } else if (isBusinessLogicError) {
        // Business logic error (like missing payment method) - don't log out, just log it
        console.warn(`API: Business logic error (403) - keeping token. Error code: ${errorCode}`);
      } else if (isAuthEndpoint) {
        // For registration/login, just log but don't redirect - let the component handle it
        console.log('API: Auth error on registration/login endpoint - letting component handle error');
      } else {
        console.error('API: Auth error on blob response - letting caller handle it');
      }
    } else if (error.response?.status >= 500) {
      // Server error - DON'T remove token, just log it
      console.warn('API: Server error (500+) - keeping token, server might be temporarily down');
    } else if (!error.response) {
      // Network error - DON'T remove token, just log it
      console.warn('API: Network error - keeping token, connection might be temporarily unavailable');
    }
    
    // Handle other errors
    if (error.response?.data?.error) {
      console.error('API Error:', error.response.data.error);
    }
    
    return Promise.reject(error);
  }
);

export default api;
